import React, { useState } from 'react';
import Survey from '../components/Survey';
import ContactForm from '../components/ContactForm';
import './Contact.css';

const Contact: React.FC = () => {
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyData, setSurveyData] = useState<any>(null);
  const [showContactForm, setShowContactForm] = useState(false);

  const handleStartProject = () => {
    setShowSurvey(true);
  };

  const handleSurveyComplete = (data: any) => {
    setSurveyData(data);
    setShowContactForm(true);
  };

  return (
    <div className="contact-page">
      <div className="container">
        {/* Hero Section */}
        <section className="contact-hero">
          <h1>Get In Touch</h1>
          <p className="hero-subtitle">
            Ready to bring your project to life? Let's discuss your vision and create something extraordinary together.
          </p>
        </section>

        {/* Contact Options */}
        <section className="contact-options">
          <div className="contact-grid">
            <div className="contact-card primary-card">
              <div className="card-icon">🚀</div>
              <h2>Start Your Project</h2>
              <p>Begin with our guided questionnaire to help us understand your needs and provide accurate project estimates.</p>
              <button onClick={handleStartProject} className="contact-cta primary">
                Start Project Survey
              </button>
              <div className="card-features">
                <div className="feature">✓ Custom project assessment</div>
                <div className="feature">✓ Accurate timeline & pricing</div>
                <div className="feature">✓ Technology recommendations</div>
              </div>
            </div>

            <div className="contact-card">
              <div className="card-icon">💬</div>
              <h2>Quick Chat</h2>
              <p>Have questions? Our AI assistant Nerdy Bot is available 24/7 to answer questions about our services.</p>
              <div className="contact-note">
                <p>👉 Look for the chat icon in the bottom-right corner</p>
              </div>
              <div className="card-features">
                <div className="feature">✓ Instant responses</div>
                <div className="feature">✓ Available 24/7</div>
                <div className="feature">✓ AI-powered assistance</div>
              </div>
            </div>

            <div className="contact-card">
              <div className="card-icon">📧</div>
              <h2>Direct Email</h2>
              <p>Prefer traditional communication? Send us an email and we'll get back to you within 24 hours.</p>
              <a href="mailto:hello@freiolabs.com" className="contact-cta secondary">
                hello@freiolabs.com
              </a>
              <div className="card-features">
                <div className="feature">✓ Detailed project discussions</div>
                <div className="feature">✓ File attachments welcome</div>
                <div className="feature">✓ 24-hour response time</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="why-choose-us">
          <h2>Why Work With FreioLabs?</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">⚡</div>
              <h3>Fast Turnaround</h3>
              <p>Agile development process ensures quick delivery without compromising quality</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">🎯</div>
              <h3>Tailored Solutions</h3>
              <p>Every project is customized to your specific needs and business goals</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">🔧</div>
              <h3>Ongoing Support</h3>
              <p>Comprehensive post-launch support to ensure your app continues to perform</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">💡</div>
              <h3>Latest Technology</h3>
              <p>We use cutting-edge tools and frameworks to future-proof your applications</p>
            </div>
          </div>
        </section>

        {/* Project Types */}
        <section className="project-types">
          <h2>What Can We Build For You?</h2>
          <div className="types-grid">
            <div className="type-card">
              <h3>📱 Mobile Apps</h3>
              <ul>
                <li>Native iOS & Android</li>
                <li>Cross-platform solutions</li>
                <li>Progressive Web Apps</li>
                <li>App Store optimization</li>
              </ul>
            </div>
            <div className="type-card">
              <h3>🌐 Web Applications</h3>
              <ul>
                <li>E-commerce platforms</li>
                <li>SaaS applications</li>
                <li>Content management systems</li>
                <li>Custom dashboards</li>
              </ul>
            </div>
            <div className="type-card">
              <h3>🤖 AI-Powered Solutions</h3>
              <ul>
                <li>Chatbots & virtual assistants</li>
                <li>Recommendation engines</li>
                <li>Data analysis tools</li>
                <li>Computer vision apps</li>
              </ul>
            </div>
            <div className="type-card">
              <h3>🔧 Custom Solutions</h3>
              <ul>
                <li>API development</li>
                <li>Database design</li>
                <li>System integrations</li>
                <li>Performance optimization</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How long does a typical project take?</h3>
              <p>Project timelines vary based on complexity. Simple apps take 4-8 weeks, while complex enterprise solutions may take 3-6 months. We'll provide a detailed timeline after assessing your requirements.</p>
            </div>
            <div className="faq-item">
              <h3>What's included in the project cost?</h3>
              <p>Our pricing includes design, development, testing, deployment, and initial support. We provide transparent pricing with no hidden fees.</p>
            </div>
            <div className="faq-item">
              <h3>Do you provide ongoing maintenance?</h3>
              <p>Yes! We offer comprehensive maintenance packages including updates, bug fixes, security patches, and feature enhancements.</p>
            </div>
            <div className="faq-item">
              <h3>Can you work with existing systems?</h3>
              <p>Absolutely. We specialize in integrating with existing systems and can help modernize legacy applications while maintaining data integrity.</p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="final-cta">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Take the first step towards bringing your vision to life. Our project survey takes just 2-3 minutes and helps us provide you with accurate estimates and recommendations.</p>
            <button onClick={handleStartProject} className="cta-button large">
              Start Your Project Survey
            </button>
            <p className="cta-note">No commitment required • Free consultation included</p>
          </div>
        </section>
      </div>

      {/* Survey and Contact Form Modals */}
      {showSurvey && !showContactForm && (
        <Survey onComplete={handleSurveyComplete} onClose={() => setShowSurvey(false)} />
      )}

      {showContactForm && (
        <ContactForm 
          surveyData={surveyData} 
          onClose={() => {
            setShowContactForm(false);
            setShowSurvey(false);
            setSurveyData(null);
          }} 
        />
      )}
    </div>
  );
};

export default Contact; 